import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IUserProfile } from '../../models';
import Avatar from '../avatar/avatar.component';
import { Flag, Ban } from 'lucide-react';
import ReportImageDialog from '../report-image-dialog/report-image-dialog.component';
import ReportAccountDialog from '../report-account-dialog/report-account-dialog.component';
import { ProfileImageType } from '../../api/report/report-profile-image.api';
import Nametag from '../cosmetics/nametag.component';
import { useCosmetic } from '../cosmetics/cosmetics-store';
import { frameRarityOf } from '../cosmetics/cosmetic-visuals';
import _get from 'lodash/get';
import { CircleCheck } from 'lucide-react';
import ButtonPrimary from '../button-refactor/button-primary';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { useFriendRequest } from '../../hooks/friend-request/useFriendRequest';
import {
  FriendRequestAction,
  FriendRequestType,
  ISendFriendRequestPayload,
  IAddFriendRequestPayload,
} from '../../types/friend';
import ButtonSecondary from '../button-refactor/button-secondary';
import JoinMembershipButton from '../membership/join-membership-button';
import { useFollow } from '../../hooks/useFollow/useFollow';
import { useAvatar } from '../../hooks/user-avatar/useAvatar';
import DialogComponent from '../dialog/dialog.component';
import { userService } from '../../services/user.service';
import toast from 'react-hot-toast';
import { Tooltip } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ListFriend from '../list-friend/list-friend.component';
import DonateUser from '../donate-user/donate-user-component';
import DialogRequest from '../dialog-request';

interface ProfileBannerProps {
  profile: IUserProfile;
  onUpdateSuccess: () => void;
  onContactSuccess?: () => void;
  changeTab?: any;
  tabActive?: any;
  userId?: string;
}

const ProfileBanner = ({
  profile,
  onUpdateSuccess,
  onContactSuccess,
  changeTab,
  tabActive,
  userId,
}: ProfileBannerProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [coverImagePreview, setCoverImagePreview] = useState<string>('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { onInputChange, imageReviewSrc, visibleUpload, onClose } = useAvatar();

  // Có gắn khung avatar → chừa thêm khoảng dưới để đế khung không che tên.
  const profileCosmetic = useCosmetic(_get(profile, 'userId', ''));
  const frameRarity = frameRarityOf(profileCosmetic?.avatarEffect);
  const hasAvatarFrame = !!frameRarity;
  // Khung ring Cổ Điển/Hiếm/Sử Thi (ava123) to hơn + gem dưới sát mép → cần khoảng cách lớn hơn kẻo đè lên nametag.
  const ringFrame = frameRarity === 'common' || frameRarity === 'rare' || frameRarity === 'epic';

  const fileRef = useRef<HTMLInputElement>(null);

  const { handleAddFriend } = useFriendRequest();
  const { handleFollow, handleUnFollow, handleUnFriend } = useFollow();

  // Trạng thái "đã gửi lời mời" (toggle được): khởi tạo từ BE, bật khi gửi, TẮT khi huỷ.
  // (Trước đây dùng `success` một chiều → huỷ xong nút vẫn kẹt "Hủy yêu cầu".)
  const [sentRequest, setSentRequest] = useState<boolean>(false);
  // Đồng bộ từ BE khi MỞ/đổi hồ sơ (F5, chuyển user). Trong phiên, gửi/huỷ do state tự quản
  // → refetch giữa chừng không ghi đè hành động vừa làm.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSentRequest(!!profile?.isFriendRequestSentByMe);
  }, [profile?.userId]);

  const userProfile = useSelector(
    (state: RootState) => state.features.auth.profile
  );

  const isUserProfile = useMemo(() => {
    return userProfile?.userId === profile.userId;
  }, [userProfile, profile]);
  const isContentCreator = useMemo(() => {
    return userProfile?.userType === 'CONTENT_CREATOR';
  }, [userProfile, profile]);

  const onAddFriend = async () => {
    if (sentRequest) {
      return;
    }

    const payload: ISendFriendRequestPayload = {
      requestUserId: _get(userProfile, 'userId', ''),
      userId: profile.userId,

      statusAction: FriendRequestAction.SEND_REQUEST,
      typeRequest: FriendRequestType.FRIEND,
    };

    await handleAddFriend(payload);
    setSentRequest(true); // phản hồi ngay: nút chuyển "Hủy yêu cầu"
    onContactSuccess?.();
  };

  const handleBlock = async () => {
    // eslint-disable-next-line no-restricted-globals
    const ok = window.confirm(
      `Chặn ${profile.fullname}?\nHai bên sẽ không thấy bài viết & trang cá nhân của nhau, không nhắn tin được, và tự huỷ kết bạn.`
    );
    if (!ok) return;
    try {
      await userService.blockUser(profile.userId);
      toast.success('Đã chặn người dùng');
      navigate('/'); // hồ sơ bị ẩn sau khi chặn → rời trang
    } catch {
      toast.error('Chặn không thành công');
    }
  };

  const handleUploadCoverImage = async () => {
    const avatarFile = fileRef?.current?.files;
    if (!avatarFile) {
      return;
    }

    try {
      setLoading(true);
      const data = await userService.updateCoverImage(avatarFile[0]);

      setCoverImagePreview(data[0].url);
      onClose();
    } catch (error) {
      toast.error('Không thể cập nhật ảnh bìa!');
    } finally {
      setLoading(false);
    }
  };
  const opentEditProfile = () => {
    document.getElementById('edit-profile')?.click();
  };

  useEffect(() => {
    const openProfile = searchParams.get('open-profile');

    if (openProfile === 'true') {
      opentEditProfile();
    }
  }, []);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [reportImageType, setReportImageType] = useState<ProfileImageType | null>(
    null
  );
  const [reportAccountOpen, setReportAccountOpen] = useState<boolean>(false);
  const handleClose = () => {
    setIsOpen(false);
  };

  // Cụm nút hành động (báo cáo/chặn/theo dõi/nhắn tin/kết bạn) — dùng ở 2 nơi:
  // desktop overlay góc dưới-phải ảnh bìa; mobile đưa xuống dòng riêng dưới avatar (tránh đè khung).
  const actionButtons = isUserProfile ? null : (
    <div className="flex gap-2 sm:gap-3 flex-wrap justify-center sm:justify-end">
      <Tooltip color="red" placement="bottom" title="Báo cáo tài khoản">
        <button
          type="button"
          aria-label="Báo cáo tài khoản"
          onClick={() => setReportAccountOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded border border-[#DEE0E6] bg-white/90 text-[#E11D48] backdrop-blur hover:bg-red-50 dark:border-slate-700 dark:bg-slate-800/90 dark:hover:bg-slate-700"
        >
          <Flag size={18} />
        </button>
      </Tooltip>
      <Tooltip color="red" placement="bottom" title="Chặn người dùng">
        <button
          type="button"
          aria-label="Chặn người dùng"
          onClick={handleBlock}
          className="flex h-10 w-10 items-center justify-center rounded border border-[#DEE0E6] bg-white/90 text-[#E11D48] backdrop-blur hover:bg-red-50 dark:border-slate-700 dark:bg-slate-800/90 dark:hover:bg-slate-700"
        >
          <Ban size={18} />
        </button>
      </Tooltip>
      <ButtonSecondary
        className="!border-[#DEE0E6] dark:border-slate-700 px-3 sm:px-6 !h-9 sm:!h-10 !font-medium !text-[#18191E] !rounded !leading-6 !text-sm sm:!text-xl flex items-center"
        onClick={async () => {
          if (profile.isFollow) {
            await handleUnFollow(profile.userId);
          } else {
            await handleFollow(profile.userId);
          }
          onContactSuccess?.();
        }}
      >
        {profile.isFollow ? 'Hủy theo dõi' : 'Theo dõi'}
      </ButtonSecondary>
      <JoinMembershipButton creatorId={profile.userId} creatorName={profile.fullname || profile.username} />

      {profile.isFriend ? (
        <div className="flex gap-2">
          <ButtonPrimary
            className="!border-[#DEE0E6] dark:border-slate-700 px-2 !h-9 sm:!h-10 !font-normal !rounded !text-sm sm:!text-xl flex !leading-6 items-center !bg-[#0057FF]"
            onClick={() => navigate(`/chat?id=${profile.userId}`)}
          >
            Nhắn tin
          </ButtonPrimary>
          <ButtonPrimary
            className="!border-[#DEE0E6] dark:border-slate-700 px-2 !h-9 sm:!h-10 !font-normal !rounded !text-sm sm:!text-xl flex !leading-6 items-center !bg-[#0057FF]"
            onClick={async () => {
              await handleUnFriend(profile.userId);
              onContactSuccess?.();
            }}
          >
            Hủy kết bạn
          </ButtonPrimary>
        </div>
      ) : profile.isFriendRequest &&
        !profile.isFriendRequestSentByMe &&
        !sentRequest ? (
        <div className="flex gap-2">
          <ButtonPrimary
            className="!border-[#DEE0E6] dark:border-slate-700 px-4 !h-9 sm:!h-10 !font-normal !rounded !text-sm sm:!text-xl flex !leading-6 items-center !bg-[#0057FF]"
            onClick={async () => {
              const payload: IAddFriendRequestPayload = {
                requestUserId: profile.userId,
                userId: _get(userProfile, 'userId', ''),
                acceptStatus: 'ACCEPTED',
              };
              await userService.addFriend(payload);
              onContactSuccess?.();
            }}
          >
            Chấp nhận
          </ButtonPrimary>
          <ButtonSecondary
            className="!border-[#DEE0E6] dark:border-slate-700 px-3 sm:px-4 !h-9 sm:!h-10 !font-normal !rounded !text-sm sm:!text-xl flex !leading-6 items-center"
            onClick={async () => {
              await handleUnFriend(profile.userId);
              onContactSuccess?.();
            }}
          >
            Từ chối
          </ButtonSecondary>
        </div>
      ) : (
        <div className="flex gap-2">
          <ButtonPrimary
            className="!border-[#DEE0E6] dark:border-slate-700 px-6 !h-9 sm:!h-10 !font-normal !rounded !text-sm sm:!text-xl flex !leading-6 items-center !bg-[#0057FF]"
            onClick={() =>
              toast.error(
                profile.isFriendRequest ? 'Yêu cầu kết bạn chưa được chấp nhận!' : 'Bạn cần kết bạn để nhắn tin!'
              )
            }
          >
            Nhắn tin
          </ButtonPrimary>
          <ButtonPrimary
            className="!border-[#DEE0E6] dark:border-slate-700 px-6 !h-9 sm:!h-10 !font-normal !rounded !text-sm sm:!text-xl flex !leading-6 items-center !bg-[#0057FF]"
            onClick={async () => {
              if (sentRequest) {
                await handleUnFriend(profile.userId);
                setSentRequest(false);
                onContactSuccess?.();
              } else {
                onAddFriend();
              }
            }}
          >
            {sentRequest ? 'Hủy yêu cầu' : 'Kết bạn'}
          </ButtonPrimary>
        </div>
      )}
    </div>
  );

  return (
    <div className="lg:-mt-10">
      <div className="relative overflow-hidden w-full lg:h-[400px] h-48">
        <img
          src={profile?.coverImage}
          alt=""
          className="h-full w-full object-cover inset-0"
        />
        {!isUserProfile && (
          <button
            type="button"
            title="Báo cáo ảnh bìa"
            onClick={() => setReportImageType('cover')}
            className="absolute bottom-3 left-3 z-20 flex items-center gap-1 rounded-full bg-black/45 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-black/60"
          >
            <Flag size={14} /> Báo cáo ảnh
          </button>
        )}
        {/* <div className="w-full bottom-0 absolute left-0 bg-gradient-to-t from-black/60 pt-20 z-10" /> */}
        {/* Nút hành động — desktop/tablet: overlay góc dưới-phải ảnh bìa (mobile: xuống dòng riêng dưới avatar) */}
        {actionButtons && (
          <div className="absolute bottom-0 right-0 m-2 sm:m-4 z-20 max-w-[calc(100%-1rem)] hidden sm:block">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
              {actionButtons}
            </div>
          </div>
        )}
        <div className="absolute bottom-3 right-3 lg:bottom-6 lg:right-[100px]">
          {isUserProfile ? (
            <>
              <Tooltip color="blue" placement="bottom" title="Đổi ảnh bìa">
                <button
                  type="button"
                  className="p-2 rounded-xl text-[#657384] cursor-pointer flex gap-2 bg-white backdrop-blur-sm text-base"
                  onClick={() => {
                    fileRef.current?.click();
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.0002 6C17.3902 6 16.8302 5.65 16.5502 5.11L15.8302 3.66C15.3702 2.75 14.1702 2 13.1502 2H10.8602C9.83017 2 8.63017 2.75 8.17017 3.66L7.45017 5.11C7.17017 5.65 6.61017 6 6.00017 6C3.83017 6 2.11017 7.83 2.25017 9.99L2.77017 18.25C2.89017 20.31 4.00017 22 6.76017 22H17.2402C20.0002 22 21.1002 20.31 21.2302 18.25L21.7502 9.99C21.8902 7.83 20.1702 6 18.0002 6ZM10.5002 7.25H13.5002C13.9102 7.25 14.2502 7.59 14.2502 8C14.2502 8.41 13.9102 8.75 13.5002 8.75H10.5002C10.0902 8.75 9.75017 8.41 9.75017 8C9.75017 7.59 10.0902 7.25 10.5002 7.25ZM12.0002 18.12C10.1402 18.12 8.62017 16.61 8.62017 14.74C8.62017 12.87 10.1302 11.36 12.0002 11.36C13.8702 11.36 15.3802 12.87 15.3802 14.74C15.3802 16.61 13.8602 18.12 12.0002 18.12Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="hidden sm:inline">Chỉnh sửa ảnh bìa</span>
                </button>
              </Tooltip>
              <input
                hidden
                type="file"
                ref={fileRef}
                onChange={onInputChange}
                accept="image/*"
              />

              <DialogComponent
                visible={visibleUpload}
                onClose={onClose}
                placement="CENTER"
                title="Đổi ảnh bìa"
                size="lg"
              >
                <div className="pt-10">
                  <img
                    src={imageReviewSrc}
                    alt=""
                    className="mx-auto h-64 object-cover"
                  />

                  <div className="mt-10 flex justify-end items-center">
                    <ButtonSecondary className="mr-4" onClick={onClose}>
                      Hủy bỏ
                    </ButtonSecondary>
                    <ButtonPrimary
                      onClick={() => handleUploadCoverImage()}
                      loading={loading}
                    >
                      Cập nhật
                    </ButtonPrimary>
                  </div>
                </div>
              </DialogComponent>
            </>
          ) : null}
        </div>
      </div>
      <div className="p-3">
        <div className="flex flex-col justify-center items-center -mt-14 sm:-mt-24 lg:-mt-50">
          <div className="relative z-10">
            <Avatar
              avatar={_get(profile, 'avatar', '')}
              label={_get(profile, 'username', '')}
              size="xxl"
              uploaded={isUserProfile}
              onUploadSuccess={onUpdateSuccess}
              preview={!isUserProfile}
              userId={_get(profile, 'userId', '')}
            />
            {!isUserProfile && (
              <button
                type="button"
                title="Báo cáo ảnh đại diện"
                onClick={() => setReportImageType('avatar')}
                className="absolute bottom-1 right-1 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/70"
              >
                <Flag size={15} />
              </button>
            )}
          </div>
          <div className={`flex justify-center items-center ${!hasAvatarFrame ? 'mt-[14px]' : ringFrame ? 'mt-[58px]' : 'mt-[26px]'}`}>
            <h3 className="relative md:text-3xl text-32x24 font-bold text-[#080809]  dark:text-white flex items-center">
              <Nametag userId={_get(profile, 'userId', '')} banner>
                {_get(profile, 'fullname', '')}
              </Nametag>
              <div className="group absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2">
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.5 11.9991L21.06 9.21906L21.4 5.53906L17.79 4.71906L15.9 1.53906L12.5 2.99906L9.1 1.53906L7.21 4.71906L3.6 5.52906L3.94 9.20906L1.5 11.9991L3.94 14.7791L3.6 18.4691L7.21 19.2891L9.1 22.4691L12.5 20.9991L15.9 22.4591L17.79 19.2791L21.4 18.4591L21.06 14.7791L23.5 11.9991ZM10.5 16.9991L6.5 12.9991L7.91 11.5891L10.5 14.1691L17.09 7.57906L18.5 8.99906L10.5 16.9991Z"
                  className={
                    _get(profile, 'userType') === 'CONTENT_CREATOR'
                      ? 'fill-[#0057FF]'
                      : 'fill-[#080809] dark:fill-slate-300'
                  }
                />
              </svg>
              <span className="absolute top-1/2  left-[calc(100%+4px)] -translate-y-1/2 whitespace-nowrap shadow-[0px_4px_20px_0px_rgba(0,0,0,0.1)] bg-[#18191E] text-white text-base p-2  rounded-lg hidden group-hover:block transition-opacity duration-300">
                {_get(profile, 'userType') === 'CONTENT_CREATOR'
                  ? 'Content Creator'
                  : 'Người dùng đã xác minh'}
              </span>
            </div>
            </h3>
          </div>
          {/* Mobile: nút hành động nằm dòng riêng dưới tên (không đè khung avatar) */}
          {actionButtons && (
            <div className="sm:hidden w-full flex flex-wrap justify-center gap-2 mt-3">
              {actionButtons}
            </div>
          )}
          {userProfile?.userId === profile.userId ? (
            !isContentCreator ? (
              <div>
                <button
                  className="button bg-[#F0F2F5] mt-1 dark:text-black px-3 py-1 hover:bg-[#0057FF] text-sm hover:text-white transition-all rounded-xl  text-red flex items-center gap-[10px] backdrop-blur-small"
                  onClick={() => setIsOpen(true)}
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.8605 3.24689L9.19385 1.87355C8.81385 1.73355 8.19385 1.73355 7.81385 1.87355L4.14719 3.24689C3.44052 3.51355 2.86719 4.34022 2.86719 5.09355V10.4936C2.86719 11.0336 3.22052 11.7469 3.65385 12.0669L7.32052 14.8069C7.96719 15.2936 9.02719 15.2936 9.67385 14.8069L13.3405 12.0669C13.7739 11.7402 14.1272 11.0336 14.1272 10.4936V5.09355C14.1339 4.34022 13.5605 3.51355 12.8605 3.24689ZM10.8205 6.98022L7.95385 9.84689C7.85385 9.94689 7.72719 9.99355 7.60052 9.99355C7.47385 9.99355 7.34719 9.94689 7.24719 9.84689L6.18052 8.76689C5.98719 8.57355 5.98719 8.25355 6.18052 8.06022C6.37385 7.86689 6.69385 7.86689 6.88719 8.06022L7.60719 8.78022L10.1205 6.26689C10.3139 6.07355 10.6339 6.07355 10.8272 6.26689C11.0205 6.46022 11.0205 6.78689 10.8205 6.98022Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span style={{ textTransform: 'none' }}>
                    Yêu cầu xác thực Content Creator
                  </span>
                </button>
                <DialogRequest handleSetOpen={handleClose} isOpen={isOpen} />
              </div>
            ) : (
              <button className="button bg-[#0057FF] px-3 py-1 mt-1 cursor-none  text-sm text-white transition-all rounded-xl  text-red flex items-center gap-[10px] backdrop-blur-small">
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.8605 3.24689L9.19385 1.87355C8.81385 1.73355 8.19385 1.73355 7.81385 1.87355L4.14719 3.24689C3.44052 3.51355 2.86719 4.34022 2.86719 5.09355V10.4936C2.86719 11.0336 3.22052 11.7469 3.65385 12.0669L7.32052 14.8069C7.96719 15.2936 9.02719 15.2936 9.67385 14.8069L13.3405 12.0669C13.7739 11.7402 14.1272 11.0336 14.1272 10.4936V5.09355C14.1339 4.34022 13.5605 3.51355 12.8605 3.24689ZM10.8205 6.98022L7.95385 9.84689C7.85385 9.94689 7.72719 9.99355 7.60052 9.99355C7.47385 9.99355 7.34719 9.94689 7.24719 9.84689L6.18052 8.76689C5.98719 8.57355 5.98719 8.25355 6.18052 8.06022C6.37385 7.86689 6.69385 7.86689 6.88719 8.06022L7.60719 8.78022L10.1205 6.26689C10.3139 6.07355 10.6339 6.07355 10.8272 6.26689C11.0205 6.46022 11.0205 6.78689 10.8205 6.98022Z"
                    fill="currentColor"
                  />
                </svg>
                <span style={{ textTransform: 'none' }}>
                  Content creator đã được xác thực
                </span>
              </button>
            )
          ) : null}
        </div>
      </div>
      <div className="flex items-center justify-center px-2 max-lg:flex-col dark:border-slate-700">
        {userProfile?.userId !== profile.userId && (
          <div className="flex items-center gap-2 text-sm py-2 pr-1 max-md:w-full lg:order-2">
            <DonateUser user={profile} />
          </div>
        )}
      </div>

      {reportImageType && (
        <ReportImageDialog
          visible={!!reportImageType}
          onClose={() => setReportImageType(null)}
          targetUserId={_get(profile, 'userId', '')}
          imageType={reportImageType}
        />
      )}

      {reportAccountOpen && (
        <ReportAccountDialog
          visible={reportAccountOpen}
          onClose={() => setReportAccountOpen(false)}
          targetUserId={_get(profile, 'userId', '')}
          targetUserName={_get(profile, 'fullname', '')}
        />
      )}
    </div>
  );
};
export default ProfileBanner;
